/* ==========================================================================
   Grandseal header dropdowns
   --------------------------------------------------------------------------
   Horizon's desktop menu opens a full-width mega menu on hover *and* on a click
   of the caret. We want a compact dropdown that opens on hover only, so for each
   top-level item that has children this script:

     - reads the child links out of Horizon's own submenu (so the Shopify
       Navigation menu stays the single source of truth),
     - builds a compact panel and marks the item .gs-nav-ready, which is what
       hides Horizon's panel and its click-to-open caret,
     - opens on pointerenter / focus and closes on pointerleave / blur / Escape.

   The Careers item has no children in the Shopify menu. Its panel is built from
   the job list that snippets/gs-site-chrome.liquid prints, so roles added in the
   admin appear in the menu without anyone touching Navigation. It is also the
   right-most item with a panel, so it is aligned to its own right edge.

   Horizon re-renders the header on its own — <overflow-list> reflows items into
   a "More" item on resize, and page transitions swap the markup — which throws
   away anything we appended. A MutationObserver watches for that and rebuilds,
   and every rebuild re-checks that a .gs-nav-ready item still actually has its
   panel rather than trusting the class.

   Pointer devices only. Touch devices get Horizon's drawer menu, which is
   separate markup and is deliberately left alone — without hover there would be
   no other way into a submenu.
   ========================================================================== */

(function () {
  'use strict';

  var CLOSE_DELAY = 140;
  var POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function caret() {
    var span = el('span', 'gs-nav__caret');
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML =
      '<svg viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
    return span;
  }

  function jobsFromJson() {
    var node = document.getElementById('gs-jobs-nav');
    if (!node) return [];
    try {
      var list = JSON.parse(node.textContent);
      return Array.isArray(list) ? list : [];
    } catch (err) {
      return [];
    }
  }

  function buildPanel(items, allLink) {
    var panel = el('div', 'gs-nav__panel');
    items.forEach(function (item) {
      var a = el('a', 'gs-nav__item');
      a.href = item.href;
      a.appendChild(document.createTextNode(item.label));
      if (item.meta) a.appendChild(el('small', null, item.meta));
      panel.appendChild(a);
    });
    if (allLink) {
      panel.appendChild(document.createElement('hr'));
      var all = el('a', 'gs-nav__all', allLink.label);
      all.href = allLink.href;
      panel.appendChild(all);
    }
    return panel;
  }

  function wire(li, panel) {
    var closeTimer = null;

    function open() {
      window.clearTimeout(closeTimer);
      panel.classList.add('is-open');
      li.classList.add('gs-nav-open');
    }

    function close() {
      panel.classList.remove('is-open');
      li.classList.remove('gs-nav-open');
    }

    function closeSoon() {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(close, CLOSE_DELAY);
    }

    li.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'touch') return;
      open();
    });

    li.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'touch') return;
      closeSoon();
    });

    li.addEventListener('focusin', open);
    li.addEventListener('focusout', function (event) {
      if (!li.contains(event.relatedTarget)) close();
    });

    li.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      close();
      var link = li.querySelector('.menu-list__link');
      if (link) link.focus();
    });

    // A click on the parent item should just follow its link, never toggle the
    // panel. Horizon binds its toggle to the caret button, which .gs-nav-ready
    // hides, but guard the whole trigger in case that ever changes.
    var trigger = li.querySelector('.menu-list__trigger');
    if (trigger) {
      trigger.addEventListener(
        'click',
        function (event) {
          var disclosure =
            event.target.closest && event.target.closest('.menu-list__disclosure');
          if (!disclosure) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          var link = li.querySelector('.menu-list__link');
          if (link && link.href) window.location.href = link.href;
        },
        true
      );
    }
  }

  function decorate(li, jobs) {
    // Trust the panel, not the class — a re-render drops one but can leave the
    // other behind.
    if (li.querySelector('.gs-nav__panel')) return;
    li.classList.remove('gs-nav-ready');

    var link = li.querySelector('.menu-list__link');
    if (!link) return;

    var href = link.getAttribute('href') || '';
    var items = [];
    var allLink = null;
    var isCareers = false;

    var native = li.querySelector('.menu-list__submenu');
    var nativeLinks = native
      ? Array.prototype.slice.call(native.querySelectorAll('a[href]'))
      : [];

    if (nativeLinks.length) {
      items = nativeLinks.map(function (a) {
        return { href: a.getAttribute('href'), label: a.textContent.trim() };
      });
    } else if (/\/pages\/careers/.test(href) && jobs.length) {
      isCareers = true;
      items = jobs.map(function (job) {
        return {
          href: '/pages/careers?job=' + encodeURIComponent(job.handle),
          label: job.title,
          meta: job.meta || ''
        };
      });
      allLink = { href: href, label: 'See all openings' };
    }

    if (!items.length) return;

    var panel = buildPanel(items, allLink);
    li.appendChild(panel);
    li.classList.add('gs-nav-ready');
    if (isCareers) li.classList.add('gs-nav-align-right');

    // Horizon draws its caret inside the disclosure button, which is now
    // hidden. Put a static one back — inside the link when there is no trigger
    // span, otherwise it lands as a block sibling and wraps onto its own line.
    var trigger = li.querySelector('.menu-list__trigger');
    var caretHost = trigger || link;
    if (!caretHost.querySelector('.gs-nav__caret')) caretHost.appendChild(caret());

    wire(li, panel);
  }

  var observer = null;
  var rebuildTimer = null;

  function init() {
    if (!POINTER.matches) return;

    var nav = document.querySelector('nav.menu-list');
    if (!nav) return;

    var jobs = jobsFromJson();

    if (observer) observer.disconnect();
    Array.prototype.forEach.call(nav.querySelectorAll('.menu-list__list-item'), function (li) {
      decorate(li, jobs);
    });

    if (!observer) {
      observer = new MutationObserver(function () {
        window.clearTimeout(rebuildTimer);
        rebuildTimer = window.setTimeout(init, 80);
      });
    }
    observer.observe(nav, { childList: true, subtree: true });
  }

  function boot() {
    init();
    // Horizon's <overflow-list> measures and reflows after layout settles.
    window.setTimeout(init, 300);
    window.setTimeout(init, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', init);
  window.addEventListener('pageshow', init);
  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(init, 60);
  });
})();